import * as React from "react";
import axios from "axios";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import FormatUnderlined from "@mui/icons-material/FormatUnderlined";
import Link from "@mui/icons-material/Link";
import TextFields from "@mui/icons-material/TextFields";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import "./CsmNotes.css";
import { useParams } from "react-router-dom";
import { API_URL } from "../../utils/auth";
import Auth from "../../utils/auth";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./quill-fonts.css";

const Font = Quill.import("formats/font");
Font.whitelist = ['arial', 'courier-new', 'georgia', 'times-new-roman', 'verdana'];
Quill.register(Font, true);

export default function ExampleTextareaComment() {
  const [notes, setNotes] = React.useState({
    actionItems: "",
    clientJourney: "",
  });
  const [loading, setLoading] = React.useState({ actionItems: false, clientJourney: false });
  const [hasChanges, setHasChanges] = React.useState({ actionItems: false, clientJourney: false });
  const fontOptions = [
    { name: "Arial", value: "arial" },
    { name: "Courier New", value: "courier-new" },
    { name: "Georgia", value: "georgia" },
    { name: "Times New Roman", value: "times-new-roman" },
    { name: "Verdana", value: "verdana" }
  ];
  const [fontMenuAnchor, setFontMenuAnchor] = React.useState(null);
  const [activeField, setActiveField] = React.useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [linkDialogField, setLinkDialogField] = React.useState(null);
  const [linkText, setLinkText] = React.useState('');
  const [linkUrl, setLinkUrl] = React.useState('');
  const [isEditingLink, setIsEditingLink] = React.useState(false);
  const [storedSelection, setStoredSelection] = React.useState(null);
  const { clientId } = useParams();
  const workspaceId = Auth.getWorkspaceId();
  const [noteIds, setNoteIds] = React.useState({
    actionItems: null,
    clientJourney: null,
  });

  const editorRefs = {
    actionItems: React.useRef(null),
    clientJourney: React.useRef(null),
  };

  const quillInstances = React.useRef({});
  const initialContent = React.useRef({
    actionItems: "",
    clientJourney: ""
  });

  const fetchData = async () => {
    try {
      if (!clientId || !workspaceId) {
        throw new Error("Missing clientId or workspaceId parameter.");
      }
      
      const notesUrl = `${API_URL}/utils/notes?clientId=${clientId}&workspaceId=${workspaceId}`;
      const notesResponse = await axios.get(notesUrl);
      const actionItemsData = notesResponse.data?.[0];
      
      const journeyUrl = `${API_URL}/utils/journey?clientId=${clientId}&workspaceId=${workspaceId}`;
      const journeyResponse = await axios.get(journeyUrl);
      const journeyData = journeyResponse.data?.[0];

      const newNotes = {
        actionItems: actionItemsData?.content || "",
        clientJourney: journeyData?.content || "",
      };

      setNotes(newNotes);
      initialContent.current = {...newNotes};

      setNoteIds({
        actionItems: actionItemsData?.id || null,
        clientJourney: journeyData?.id || null,
      });
    } catch (error) {
      console.error("Error in fetchData:", error.response?.data || error.message);
    }
  };

  const sendData = async (field) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const noteId = noteIds[field];
      const noteContent = notes[field] || "";
      const endpoint = field === "clientJourney" ? "journey" : "notes";

      // Validar contenido
      const isEmpty = !noteContent || noteContent === "<p><br></p>" || noteContent.trim() === "";
      if (isEmpty) {
        alert("Please enter some content before saving");
        return;
      }

      const payload = {
        ...(noteId && { id: noteId }),
        content: noteContent,
        clientId: parseInt(clientId),  
        workspaceId: parseInt(workspaceId),
        updatedAt: new Date().toISOString()
      };

      console.log("Sending payload:", payload); 
      if (!payload.clientId || !payload.content) {
        throw new Error("Missing required fields: clientId or content");
      }

      setLoading((prev) => ({ ...prev, [field]: true }));

      const url = `${API_URL}/utils/${endpoint}`;
      const response = noteId 
        ? await axios.put(url, payload) 
        : await axios.post(url, payload);

      if (!noteId) {
        setNoteIds(prev => ({ ...prev, [field]: response.data.id }));
      }

      setHasChanges(prev => ({ ...prev, [field]: false }));
      initialContent.current[field] = noteContent;

    } catch (error) {
      console.error(`Error updating ${field}:`, error.response?.data || error.message);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSend = (field) => {
    sendData(field);
  };

  const openFontMenu = (event, field) => {
    if (fontMenuAnchor && activeField === field) {
      closeFontMenu();
    } else {
      setFontMenuAnchor(event.currentTarget);
      setActiveField(field);
    }
  };

  const closeFontMenu = () => {
    setFontMenuAnchor(null);
    setActiveField(null);
  };

  const applyStyle = (field, command, value = null) => {
    const quillInstance = quillInstances.current[field];
    if (quillInstance) {
      quillInstance.focus();
      const selection = quillInstance.getSelection();
      if (selection && selection.length > 0) {
        if (command === "font") {
          quillInstance.format("font", value);
        } else {
          const currentFormat = quillInstance.getFormat(selection);
          quillInstance.format(command, !currentFormat[command]);
        }
      }
    }
  };

  const handleLinkClick = (field) => {
    const quillInstance = quillInstances.current[field];
    if (!quillInstance) return;

    quillInstance.focus();
    
    setTimeout(() => {
      const selection = quillInstance.getSelection(true);
      let currentSelection = selection;
      
      if (!currentSelection || currentSelection.length === 0) {
        const length = quillInstance.getLength();
        currentSelection = { index: length, length: 0 };
        quillInstance.setSelection(currentSelection.index, 0);
      }

      setStoredSelection({ ...currentSelection, field });

      const selectedText = currentSelection && currentSelection.length > 0 
        ? quillInstance.getText(currentSelection.index, currentSelection.length).trim()
        : '';
      
      const currentFormat = quillInstance.getFormat(currentSelection);
      const existingLink = currentFormat.link;

      setLinkDialogField(field);
      
      if (existingLink) {
        setIsEditingLink(true);
        setLinkText(selectedText || '');
        setLinkUrl(existingLink);
      } else if (selectedText) {
        setIsEditingLink(false);
        setLinkText(selectedText);
        setLinkUrl('');
      } else {
        setIsEditingLink(false);
        setLinkText('');
        setLinkUrl('');
      }
      
      setLinkDialogOpen(true);
    }, 0);
  };

  const handleLinkDialogSave = () => {
    if (!linkDialogField) return;
    
    const quillInstance = quillInstances.current[linkDialogField];
    if (!quillInstance) return;

    quillInstance.focus();
    
    const selection = storedSelection && storedSelection.field === linkDialogField 
      ? storedSelection 
      : quillInstance.getSelection(true);
    
    if (isEditingLink) {
      if (!linkUrl || !linkUrl.trim()) {
        if (selection) {
          quillInstance.setSelection(selection.index, selection.length || 1);
          quillInstance.format('link', false);
        }
      } else {
        const formattedUrl = formatUrl(linkUrl.trim());
        if (selection) {
          quillInstance.setSelection(selection.index, selection.length || 1);
          quillInstance.format('link', formattedUrl);
        }
      }
    } else {
      if (!linkUrl || !linkUrl.trim()) {
        handleLinkDialogCancel();
        return;
      }
      
      const formattedUrl = formatUrl(linkUrl.trim());
      
      if (selection && selection.length > 0) {
        quillInstance.setSelection(selection.index, selection.length);
        quillInstance.format('link', formattedUrl);
      } else if (linkText && linkText.trim()) {
        const insertIndex = selection ? selection.index : quillInstance.getLength();
        quillInstance.insertText(insertIndex, linkText.trim(), 'link', formattedUrl);
        quillInstance.setSelection(insertIndex + linkText.trim().length);
      } else {
        const insertIndex = selection ? selection.index : quillInstance.getLength();
        const defaultText = formattedUrl.replace(/^https?:\/\//, '').replace(/^mailto:/, '').replace(/^tel:/, '');
        quillInstance.insertText(insertIndex, defaultText, 'link', formattedUrl);
        quillInstance.setSelection(insertIndex + defaultText.length);
      }
    }
    
    handleLinkDialogCancel();
  };

  const handleLinkDialogCancel = () => {
    setLinkDialogOpen(false);
    setLinkText('');
    setLinkUrl('');
    setIsEditingLink(false);
    setLinkDialogField(null);
    setStoredSelection(null);
  };

  const formatUrl = (url) => {
    if (!url) return url;
    
    url = url.trim();
    
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:') || url.startsWith('tel:')) {
      return url;
    }
    
    if (url.includes('@') && !url.includes(' ')) {
      return `mailto:${url}`;
    }
    
    if (url.match(/^\+?[\d\s\-\(\)]+$/) && !url.includes(' ')) {
      return `tel:${url}`;
    }
    
    return `https://${url}`;
  };

  React.useEffect(() => {
    Object.keys(editorRefs).forEach((field) => {
      if (editorRefs[field].current && !quillInstances.current[field]) {
        const quillInstance = new Quill(editorRefs[field].current, {
          theme: "snow",
          modules: {
            toolbar: false,
          },
        });

        quillInstances.current[field] = quillInstance;

        quillInstance.root.addEventListener('click', (e) => {
          const link = e.target.closest('a');
          if (link && !e.ctrlKey && !e.metaKey) {
            const href = link.getAttribute('href');
            if (href) {
              window.open(href, '_blank', 'noopener,noreferrer');
              e.preventDefault();
              e.stopPropagation();
            }
          }
        });

        quillInstance.on("text-change", () => {
          const content = quillInstance.root.innerHTML;
          setNotes((prev) => ({
            ...prev,
            [field]: content,
          }));
          
          setHasChanges(prev => ({
            ...prev,
            [field]: content !== initialContent.current[field]
          }));
        });
      }
    });

    return () => {
      Object.keys(quillInstances.current).forEach((field) => {
        if (quillInstances.current[field]) {
          quillInstances.current[field] = null;
        }
      });
    };
  }, []);

  React.useEffect(() => {
    Object.keys(editorRefs).forEach((field) => {
      if (quillInstances.current[field]) {
        if (quillInstances.current[field].root.innerHTML !== notes[field]) {
          quillInstances.current[field].root.innerHTML = notes[field] || "";
        }
      }
    });
  }, [notes]);

  React.useEffect(() => {
    if (clientId && workspaceId) {
      fetchData();
    } else {
      console.error("Missing clientId or workspaceId parameter.");
    }
  }, [clientId, workspaceId]);

  return (
    <Box className="card-container">
      {[
        {
          field: "actionItems",
          label: "CSM Notes",
        },
        {
          field: "clientJourney",
          label: "Client Journey",
        },
      ].map(({ field, label }, index) => (
        <FormControl className="comment-box" key={index}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <FormLabel
              sx={{
                fontFamily: "Albert Sans",
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              {label}
            </FormLabel>
            <Button
              className="send-button"
              variant="contained"
              onClick={() => handleSend(field)}
              disabled={!clientId || loading[field] || !hasChanges[field]}
              sx={{
                textTransform: 'none',
                backgroundColor: hasChanges[field] ? '#572AF1' : '#888888',
                '&:hover': { backgroundColor: hasChanges[field] ? '#4A21D1' : '#888888' },
              }}
            >
              {loading[field] ? "Saving..." : "Save"}
            </Button>
          </Box>
          <Box className="toolbar" sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => applyStyle(field, "bold")}
              sx={{ color: "#333" }}
            >
              <FormatBold />
            </IconButton>
            <IconButton
              onClick={() => applyStyle(field, "italic")}
              sx={{ color: "#333" }}
            >
              <FormatItalic />
            </IconButton>
            <IconButton
              onClick={() => applyStyle(field, "underline")}
              sx={{ color: "#333" }}
            >
              <FormatUnderlined />
            </IconButton>
            <IconButton
              onClick={() => handleLinkClick(field)}
              sx={{ color: "#333" }}
              title="Insert/Edit Link"
            >
              <Link />
            </IconButton>
            <IconButton onClick={(event) => openFontMenu(event, field)}>
              <TextFields />
              <KeyboardArrowDown />
            </IconButton>
            <Menu
              anchorEl={fontMenuAnchor}
              open={Boolean(fontMenuAnchor) && activeField === field}
              onClose={closeFontMenu}
            >
              {fontOptions.map((font) => (
                <MenuItem
                  key={font.value}
                  onClick={() => {
                    applyStyle(field, "font", font.value);
                    closeFontMenu();
                  }}
                  style={{ fontFamily: font.name }}
                >
                  {font.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          {/* Quill Editor */}
          <div
            ref={editorRefs[field]}
            style={{
              width: "100%",
              minHeight: "150px",
              maxHeight: "200px",
              border: "1px solid #ddd",
              borderRadius: "20px",
              padding: "8px",
              overflowY: "auto",
            }}
          />
        </FormControl>
      ))}
      
      <Dialog 
        open={linkDialogOpen} 
        onClose={handleLinkDialogCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '400px',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', padding: '24px 24px 16px 24px' }}>
          {isEditingLink ? 'Edit Link' : 'Insert Link'}
        </DialogTitle>
        <DialogContent sx={{ padding: '0 24px' }}>
          {!isEditingLink && (
            <TextField
              autoFocus
              margin="dense"
              label="Link Text"
              type="text"
              fullWidth
              variant="outlined"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              sx={{ 
                marginTop: '8px',
                marginBottom: '16px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '& fieldset': {
                    borderColor: '#ddd',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': {
                    borderColor: '#999'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4A21D1',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4A21D1'
                }
              }}
              placeholder="Enter text to display"
            />
          )}
          <TextField
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            sx={{ 
              marginTop: '8px',
              marginBottom: '8px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                '& fieldset': {
                  borderColor: '#ddd',
                  borderWidth: '1px'
                },
                '&:hover fieldset': {
                  borderColor: '#999'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4A21D1',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#4A21D1'
              }
            }}
            placeholder="https://example.com"
            helperText={isEditingLink ? "Leave empty to remove link" : "Enter the URL"}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: '8px' }}>
          <Button 
            onClick={handleLinkDialogCancel}
            variant="contained"
            sx={{ 
              textTransform: 'none',
              backgroundColor: '#9e9e9e',
              color: '#fff',
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#757575'
              }
            }}
          >
            Cancel
          </Button>
          {isEditingLink && (
            <Button
              onClick={() => {
                if (linkDialogField) {
                  const quillInstance = quillInstances.current[linkDialogField];
                  if (quillInstance) {
                    quillInstance.format('link', false);
                  }
                }
                handleLinkDialogCancel();
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '8px 24px',
                '&:hover': {
                  backgroundColor: '#c62828'
                }
              }}
            >
              Remove Link
            </Button>
          )}
          <Button 
            onClick={handleLinkDialogSave}
            variant="contained"
            sx={{ 
              backgroundColor: "#4A21D1",
              color: '#fff',
              "&:hover": { 
                backgroundColor: "#3a1aa1" 
              },
              textTransform: 'none',
              padding: '8px 24px'
            }}
          >
            {isEditingLink ? 'Update' : 'Insert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}